import React, { useEffect, useState } from 'react';
import { Modal } from "react-bootstrap";
import moment from 'moment';
import toast from 'react-hot-toast';
import CustomSingleSelect, { CustomSingleSelectModel } from '../../../components/CustomSingleSelect';
import CustomDatePicker from '../../../components/CustomDatePicker';
import CustomTextInput from '../../../components/CustomTextInput';
import { PersonnelService } from '../../../services/PersonnelService';
import { DriverService } from '../../../services/DriverService';
import { VehicleService } from '../../../services/VehicleService';
import { initialMesaiNoktasiListIstegi, MesaiNoktasiService } from '../../../services/MesaiNoktasiService';
import { ElleEkleIstegi, IS_TIPLERI } from '../../../services/MesaiKaydiService';

interface Props {
    isShown: boolean
    onConfirm: (_istek: ElleEkleIstegi) => void
    onClose: () => void
}

/** QR noktaları seçimi: standart liste ucundan aktif noktalar. */
const noktalariGetir = async () => {
    var response = await MesaiNoktasiService.getListPagingAndFilter({ ...initialMesaiNoktasiListIstegi, take: 1000 });

    return {
        ...response,
        // tur (1 Depo, 2 Ofis) ek değer olarak taşınır: iş tipi yalnız depo noktasında seçilir.
        data: response.isSuccess ? (response.data?.list ?? []).filter(x => x.isCustomActive).map(x => ({ label: x.tur == 2 ? x.ad + ' (Ofis)' : x.ad, value: x.mesaiNoktasiId, tur: String(x.tur ?? 1) })) : []
    };
}

const saatGirdisi = (labelText: string, value: string, setValue: (s: string) => void, isLabelRequired: boolean) => (
    <React.Fragment>
        <label className={"form-label " + (isLabelRequired ? 'required' : '')}>{labelText}</label>
        <input
            type="time"
            className="form-control form-control-solid"
            style={{ border: '1px solid #F1F1F4' }}
            value={value}
            onChange={(e) => setValue(e.target.value)}
        />
    </React.Fragment>
)

/**
 * Mesai Kayıtları > Elle Ekle (MesaiKaydi/ElleEkle). QR okutulamadığında yetkili kaydı elle girer (Kaynak = Elle).
 * Personel: QR noktası + iş tipi; Sürücü: araç (iş tipi yok, yalnız süre).
 * Faz 2 (18.10.2026): iş tipi (Lojistik / Ambalaj) yalnız depo noktasında seçilir (QR yerine elle); ofis noktasında iş tipi yok.
 */
const ElleEkleModal = ({ isShown, onConfirm, onClose }: Props) => {
    const [tur, setTur] = useState<number>(1);
    const [kisi, setKisi] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [nokta, setNokta] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [arac, setArac] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [tarih, setTarih] = useState<string | undefined>(moment().format('YYYY-MM-DD'));
    const [giris, setGiris] = useState<string>('');
    const [cikis, setCikis] = useState<string>('');
    const [isTipi, setIsTipi] = useState<number | undefined>(undefined);
    const [aciklama, setAciklama] = useState<string | undefined>('');

    useEffect(() => {
        if (isShown) {
            setTur(1);
            setKisi(undefined);
            setNokta(undefined);
            setArac(undefined);
            setTarih(moment().format('YYYY-MM-DD'));
            setGiris('');
            setCikis('');
            setIsTipi(undefined);
            setAciklama('');
        }
    }, [isShown])

    const turDegisti = (yeni: number) => {
        if (yeni == tur)
            return;

        setTur(yeni);
        setKisi(undefined);
        setNokta(undefined);
        setArac(undefined);
        setIsTipi(undefined);
    }

    const ofis = tur == 1 && nokta?.additionalValue1 == '2';
    const isTipiGerekli = tur == 1 && !ofis;

    const noktaDegisti = (v: CustomSingleSelectModel | undefined) => {
        setNokta(v);

        if (v?.additionalValue1 == '2')
            setIsTipi(undefined);
    }

    const kaydet = () => {
        if (!kisi) {
            toast.error(tur == 1 ? 'Personel seçiniz.' : 'Sürücü seçiniz.');
            return;
        }

        if (tur == 1 && !nokta) {
            toast.error('QR noktası seçiniz.');
            return;
        }

        if (tur == 2 && !arac) {
            toast.error('Araç seçiniz.');
            return;
        }

        if (!tarih) {
            toast.error('Tarih alanı zorunludur.');
            return;
        }

        if (!giris) {
            toast.error('Giriş saati zorunludur.');
            return;
        }

        if (isTipiGerekli && !isTipi) {
            toast.error('Depo kaydında iş tipi seçiniz (Lojistik / Ambalaj).');
            return;
        }

        if (!aciklama || aciklama.trim() == '') {
            toast.error('Açıklama zorunludur (elle kayıt nedeni).');
            return;
        }

        onConfirm({
            tur: tur,
            personnelId: tur == 1 ? kisi.value : null,
            driverId: tur == 2 ? kisi.value : null,
            mesaiNoktasiId: tur == 1 ? nokta?.value ?? null : null,
            vehicleId: tur == 2 ? arac?.value ?? null : null,
            tarih: tarih,
            girisSaati: giris,
            cikisSaati: cikis || null,
            isTipi: isTipiGerekli ? isTipi ?? null : null,
            aciklama: aciklama.trim()
        });
    }

    return (
        <Modal
            tabIndex={-1}
            dialogClassName='modal-dialog modal-dialog-centered mw-650px'
            show={isShown}
            onHide={onClose}
            backdrop={'static'}
            keyboard={false}
        >
            <div className='modal-header'>
                <h2 className='mb-0'>Elle Mesai Kaydı</h2>
            </div>

            <div className='modal-body'>
                <div className='mb-5'>
                    <label className="form-label required">Tür</label>
                    <div className='d-flex'>
                        {
                            [{ v: 1, t: 'Personel (depo / ofis)' }, { v: 2, t: 'Sürücü (araç)' }].map(x =>
                                <label key={x.v} className='form-check form-check-custom form-check-solid me-10'>
                                    <input className='form-check-input' type='radio' checked={tur == x.v} onChange={() => turDegisti(x.v)} />
                                    <span className='form-check-label fw-bold'>{x.t}</span>
                                </label>
                            )
                        }
                    </div>
                </div>

                <div className='row mb-5'>
                    <div className='col-md-6'>
                        {
                            tur == 1
                                ? <CustomSingleSelect key='personel' labelText='Personel' isLabelRequired selectedValue={kisi} onChange={setKisi} onMenuOpen={() => PersonnelService.getPersonnelList(undefined, true)} labelName='nameSurname' valueName='personnelId' />
                                : <CustomSingleSelect key='surucu' labelText='Sürücü' isLabelRequired selectedValue={kisi} onChange={setKisi} onMenuOpen={() => DriverService.getDriverList()} labelName='nameSurname' valueName='driverId' />
                        }
                    </div>

                    <div className='col-md-6'>
                        {
                            tur == 1
                                ? <CustomSingleSelect key='nokta' labelText='QR Noktası' isLabelRequired selectedValue={nokta} onChange={noktaDegisti} onMenuOpen={noktalariGetir} labelName='label' valueName='value' additionalValue1Name='tur' />
                                : <CustomSingleSelect key='arac' labelText='Araç' isLabelRequired selectedValue={arac} onChange={setArac} onMenuOpen={() => VehicleService.getVehicleList()} labelName='plate' valueName='vehicleId' />
                        }
                    </div>
                </div>

                <div className='row mb-5'>
                    <div className='col-md-4'>
                        <CustomDatePicker labelText='Tarih (giriş günü)' isLabelRequired value={tarih} setValue={setTarih} />
                    </div>

                    <div className='col-md-4'>
                        {saatGirdisi('Giriş', giris, setGiris, true)}
                    </div>

                    <div className='col-md-4'>
                        {saatGirdisi('Çıkış', cikis, setCikis, false)}
                    </div>
                </div>

                {
                    isTipiGerekli && (
                        <div className='mb-5'>
                            <label className="form-label required">İş tipi (depo)</label>
                            <div className='d-flex'>
                                {
                                    IS_TIPLERI.map(x =>
                                        <label key={x.value} className='form-check form-check-custom form-check-solid me-10'>
                                            <input className='form-check-input' type='radio' checked={isTipi == x.value} onChange={() => setIsTipi(x.value)} />
                                            <span className='form-check-label fw-bold'>{x.label}</span>
                                        </label>
                                    )
                                }
                            </div>
                        </div>
                    )
                }

                <div className='mb-5'>
                    <CustomTextInput labelText='Açıklama' isLabelRequired value={aciklama} setValue={setAciklama} />
                </div>

                <div className='alert alert-primary d-flex align-items-start p-4 mb-0'>
                    <i className='fa fa-info-circle text-primary fs-3 me-3 mt-1'></i>
                    <div className='fs-7'>
                        Çıkış boş bırakılırsa kayıt açık kalır. Çıkış girişten önceyse gece yarısını geçmiş sayılır ve giriş gününe yazılır.
                        {' '}Normal / ek mesai çalışma saatine göre hesaplanır; Lojistik mesai uyan ek mesai talebine yerleşir, yerleşmeyen ek mesai Atanacak Mesailer'e düşer.
                        {' '}Ofis kaydında iş tipi yoktur (yalnız puantaj). Sürücüde yalnız süre tutulur.
                    </div>
                </div>
            </div>

            <div className="modal-footer">
                <button onClick={onClose} type="button" className="btn btn-light">
                    Vazgeç
                </button>
                <button onClick={kaydet} type="button" className="btn btn-success">
                    Kaydet
                </button>
            </div>
        </Modal>
    )
}

export default ElleEkleModal;
